import React, { useState } from 'react'
import { BuildingOffice2Icon, CheckIcon, ArrowRightIcon, SparklesIcon, PaintBrushIcon, CubeIcon } from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'
import api from '../config/api'

const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className={className}
  >
    {children}
  </motion.div>
)

const partnerOrgs = [
  'Brushamania',
  'Scarborough Rotary Passport Club',
  'DURHAM ONTARIO DENTAL SOCIETY',
  'Uxbridge family dentistry',
]

const BulkOrders = () => {
  const [formState, setFormState] = useState({
    name: '',
    organization: '',
    email: '',
    quantity: '',
    message: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const [customForm, setCustomForm] = useState({
    clinicName: '',
    email: '',
    requirements: '',
  })
  const [customSubmitted, setCustomSubmitted] = useState(false)
  const [customLoading, setCustomLoading] = useState(false)

  const handleChange = (e) => {
    setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const [formError, setFormError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setFormError('')
    try {
      await api.post('/bulk_quotes.php', formState)
      setSubmitted(true)
    } catch (err) {
      setFormError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const [customError, setCustomError] = useState('')

  const handleCustomSubmit = async (e) => {
    e.preventDefault()
    setCustomLoading(true)
    setCustomError('')
    try {
      await api.post('/custom_inquiries.php', customForm)
      setCustomSubmitted(true)
    } catch (err) {
      setCustomError(err.message)
    } finally {
      setCustomLoading(false)
    }
  }

  const useCases = [
    { icon: '', label: 'Dental Clinics' },
    { icon: '', label: 'Oral Health Outreach Programs' },
    { icon: '', label: 'Community Health Initiatives' },
    { icon: '', label: 'NGOs and Schools' },
    { icon: '', label: 'Oral Health Awareness Events' },
  ]

  const steps = [
    { num: '01', title: 'Submit Your Quote Request', desc: 'Fill out the form with your clinic name, email, and quantity needed.' },
    { num: '02', title: 'Receive Your Custom Quote', desc: 'Our team reviews your request and sends a personalized bulk pricing quote within 48 hours.' },
    { num: '03', title: 'Confirm & Pay', desc: 'Approve the quote, upload your clinic logo (optional), and complete payment.' },
    { num: '04', title: 'Delivery in 3–4 Weeks', desc: 'Your order ships to your Canadian address within 3–4 weeks of confirmation.' },
  ]

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-800 to-emerald-950 text-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Text & CTA */}
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-900/60 border border-emerald-600/30 text-white text-xs font-semibold shadow-sm animate-pulse">
                With min 144 toothbrushes
              </span>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
                Bulk Orders for <br />
                <span className="text-emerald-300">Clinics, Hotels & Organizations</span>
              </h1>
              <p className="text-emerald-100/90 text-base sm:text-lg leading-relaxed max-w-2xl font-medium">
                Twiggle Oral Care supplies premium bamboo toothbrushes for businesses, healthcare providers, hospitality groups, and community initiatives looking for sustainable oral care solutions.
              </p>
              
              <ul className="space-y-3.5 pt-2">
                {[
                  'Dental clinics',
                  'Oral health outreach programs',
                  'NGOs, schools & community organizations',
                  'Corporate events & wellness initiatives',
                  'Hotels, resorts & guest amenities',
                  'Retail stores & wellness boutiques',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-5 h-5 bg-emerald-900/40 rounded-full flex items-center justify-center border border-emerald-700/50 text-[10px] text-emerald-300">
                      •
                    </span>
                    <span className="text-white text-sm sm:text-base font-medium">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-4">
                <button
                  onClick={() => {
                    const el = document.getElementById('bulk-name');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      el.focus();
                    }
                  }}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-800 font-bold rounded-xl shadow-lg hover:bg-emerald-50 hover:scale-[1.02] transition-all duration-300"
                >
                  Request Bulk Quote
                  <ArrowRightIcon className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>

            {/* Right Column: Responsive Product Image */}
            <div className="lg:col-span-5 relative">
              <div className="absolute -inset-2 bg-emerald-500/10 rounded-3xl blur-2xl animate-pulse" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 group bg-emerald-900/20">
                <img
                  src="/images/project/custom-engraved-brushes.jpeg"
                  alt="Twiggle personalized bamboo toothbrushes bulk orders mockup"
                  className="w-full object-cover h-64 sm:h-80 md:h-96 lg:h-[420px] transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute bottom-4 left-4 bg-emerald-800/90 backdrop-blur-md text-white px-3.5 py-1.5 rounded-lg shadow-lg text-xs font-semibold border border-emerald-600/30">
                  100% Biodegradable Handles
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Process + Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Process Steps */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-8">How It Works</h2>
              <div className="space-y-8">
                {steps.map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="flex gap-5"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-primary-100 text-primary-700 font-extrabold text-sm flex items-center justify-center">
                      {step.num}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-base">{step.title}</h3>
                      <p className="text-gray-500 text-sm mt-1 leading-relaxed">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Testimonials replacement */}
              <div className="mt-10 space-y-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-primary-600" /> Trusted by Dental Professionals
                </h3>
                {[
                  {
                    quote:
                      "We introduced Twiggle bamboo toothbrushes into our patient hygiene kits, and the response has been extremely positive. Patients have shared that they love the soft bristles, comfortable feel, and eco-friendly packaging. It's rewarding to offer a product that not only supports oral health but also makes patients feel good about making a sustainable choice.",
                    name: 'Dr. Mukarram Muneer DDS (NYU)',
                    avatar: '/images/project/dr.jpeg',
                  },
                  {
                    quote:
                      'Twiggle has been a wonderful partner for our clinic. The personalized bamboo brushes help us stand out, and patients consistently ask where they can get more. Quality and sustainability in one package.',
                    name: 'Dr. Sarah Chen, DDS',
                    avatar: '/images/project/ceo.png',
                  },
                  {
                    quote:
                      'We use Twiggle brushes for community outreach and school visits. Parents appreciate the eco-friendly message, and kids love the kid-sized options. Highly recommend for any practice focused on preventive care.',
                    name: 'Dr. James Patel, DMD',
                    avatar: '/images/project/dental.png',
                  }
                ].map((t, idx) => (
                  <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-4">
                    <p className="text-sm text-gray-600 italic leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <img
                        src={t.avatar}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover bg-primary-100 border border-gray-200"
                      />
                      <span className="text-xs font-bold text-gray-800">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote Request Form */}
            <div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <CheckIcon className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Quote Request Received!</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                      Thank you for your interest. Our team will reach out within <strong>48 hours</strong> with a customized quote.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setFormState({ name: '', organization: '', email: '', quantity: '', message: '' }) }}
                      className="mt-6 px-6 py-2.5 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 font-medium transition-colors"
                    >
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Request a Bulk Quote</h2>
                    <p className="text-gray-500 text-sm mb-7">For orders of 1,000+ toothbrushes. We'll respond within 48 hours.</p>

                    <form onSubmit={handleSubmit} className="space-y-5">

                      {formError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                          {formError}
                        </div>
                      )}

                      {/* Name */}
                      <div>
                        <label htmlFor="bulk-name" className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Full Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          id="bulk-name"
                          name="name"
                          type="text"
                          required
                          value={formState.name}
                          onChange={handleChange}
                          placeholder="Dr. Jane Smith"
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none transition-colors"
                        />
                      </div>

                      {/* Organization */}
                      <div>
                        <label htmlFor="bulk-org" className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Clinic / Organization <span className="text-red-400">*</span>
                        </label>
                        <input
                          id="bulk-org"
                          name="organization"
                          type="text"
                          required
                          value={formState.organization}
                          onChange={handleChange}
                          placeholder="Smile Dental Clinic"
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none transition-colors"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="bulk-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Email Address <span className="text-red-400">*</span>
                        </label>
                        <input
                          id="bulk-email"
                          name="email"
                          type="email"
                          required
                          value={formState.email}
                          onChange={handleChange}
                          placeholder="you@clinic.com"
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none transition-colors"
                        />
                      </div>

                      {/* Quantity */}
                      <div>
                        <label htmlFor="bulk-qty" className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Quantity Required <span className="text-red-400">*</span>
                        </label>
                        <select
                          id="bulk-qty"
                          name="quantity"
                          required
                          value={formState.quantity}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none transition-colors"
                        >
                          <option value="">Select approximate quantity</option>
                          <option value="1000-2000">1,000 – 2,000 toothbrushes</option>
                          <option value="2000-5000">2,000 – 5,000 toothbrushes</option>
                          <option value="5000-10000">5,000 – 10,000 toothbrushes</option>
                          <option value="10000+">10,000+ toothbrushes</option>
                        </select>
                      </div>

                      {/* Message */}
                      <div>
                        <label htmlFor="bulk-message" className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Message / Requirements <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                          id="bulk-message"
                          name="message"
                          rows={4}
                          value={formState.message}
                          onChange={handleChange}
                          placeholder="Tell us about your project, preferred toothbrush types, engraving needs, or delivery timeline..."
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none resize-none transition-colors"
                        />
                      </div>

                      <button
                        id="bulk-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 py-4 px-8 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-bold rounded-xl transition-all duration-300"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Request Quote
                            <ArrowRightIcon className="w-4 h-4" />
                          </>
                        )}
                      </button>

                      <p className="text-center text-xs text-gray-400">
                        By submitting, you agree to be contacted by our team within 48 business hours.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Clinic-Branded Packaging</h2>
              <p className="text-gray-600 leading-relaxed">
                See how practices like Smile Dental Care present Twiggle bamboo toothbrushes with fully customized packaging and engraved handles for patients.
              </p>
            </div>
            <img
              src="/images/project/smile-dental-green.jpeg"
              alt="Smile Dental Care clinic with custom Twiggle toothbrush packaging"
              className="rounded-2xl shadow-lg w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 sm:py-20 bg-gray-50 border-t border-gray-100" aria-label="Chosen by dental and community organizations">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">Chosen by Dental & Community Organizations</h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-8">
              Twiggle Oral Care bamboo toothbrushes are being used by clinics, community organizations, and wellness initiatives committed to sustainability.
            </p>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Including:</p>
            <ul className="flex flex-wrap justify-center gap-3">
              {partnerOrgs.map((org) => (
                <li key={org} className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-800 shadow-sm hover:scale-105 transition-all duration-200 cursor-default">
                  {org}
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* ══════════ FULLY CUSTOM PROJECTS ══════════ */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Content Sidebar */}
            <div className="space-y-8">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold mb-4">
                  <PaintBrushIcon className="w-3.5 h-3.5" />
                  Ultimate Customization
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                  Custom Brush & <br />
                  <span className="text-primary-600">Custom Box Packaging</span>
                </h2>
                <p className="mt-5 text-gray-600 text-lg leading-relaxed">
                  Go beyond logo engraving. We offer end-to-end customization for dental clinics and organizations that want a truly signature product.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  {
                    icon: <PaintBrushIcon className="w-5 h-5" />,
                    title: 'Custom Brush Design',
                    desc: 'Choose specific handle colors, bristle combinations, and unique ergonomic shapes tailored to your brand.'
                  },
                  {
                    icon: <CubeIcon className="w-5 h-5" />,
                    title: 'Fully Branded Packaging',
                    desc: 'Work with our designers to create unique retail-ready boxes that match your clinical aesthetic perfectly.'
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-50 text-primary-600 flex items-center justify-center border border-gray-100">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-gray-500 text-sm mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Custom Branded Box Photo next to form */}
              <div className="mt-8">
                <img
                  src="/images/project/smile-dental-green.jpeg"
                  alt="Smile Dental Care clinic with green custom Twiggle toothbrush box and engraved toothbrush"
                  className="rounded-2xl shadow-md w-full object-cover max-h-72"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Inquiry Form */}
            <div className="relative">
              <div className="absolute -inset-4 bg-primary-50 rounded-[2.5rem] -rotate-1 hidden lg:block" />
              <div className="relative bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10">
                {customSubmitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <CheckIcon className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Custom Project Inquiry Sent!</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                      Our design team will review your requirements and reach out within 48 hours.
                    </p>
                    <button
                      onClick={() => setCustomSubmitted(false)}
                      className="mt-6 px-6 py-2.5 text-sm text-primary-600 border border-primary-100 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Send Another Inquiry
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Custom Project Inquiry</h3>
                    <form onSubmit={handleCustomSubmit} className="space-y-4">
                      {customError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                          {customError}
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Clinic Name</label>
                        <input 
                          type="text" 
                          required 
                          value={customForm.clinicName}
                          onChange={(e) => setCustomForm({...customForm, clinicName: e.target.value})}
                          className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3.5 text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                          placeholder="Your Clinic Name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Contact Email</label>
                        <input 
                          type="email" 
                          required 
                          value={customForm.email}
                          onChange={(e) => setCustomForm({...customForm, email: e.target.value})}
                          className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3.5 text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                          placeholder="email@clinic.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Project Requirements</label>
                        <textarea 
                          rows={4}
                          required 
                          value={customForm.requirements}
                          onChange={(e) => setCustomForm({...customForm, requirements: e.target.value})}
                          className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3.5 text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-all resize-none"
                          placeholder="Tell us about the colors, shapes, and box designs you have in mind..."
                        />
                      </div>
                      <button 
                        type="submit"
                        disabled={customLoading}
                        className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl shadow-lg shadow-primary-900/20 transition-all flex items-center justify-center gap-2"
                      >
                        {customLoading ? 'Sending...' : 'Submit Custom Inquiry'}
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  )
}

export default BulkOrders
